import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Printer, Upload } from "lucide-react";
import { noticeApi } from "../../services/noticeApi";
import { StatusBadge } from "../../components/StatusBadge";
import { useLocalization } from "../../hooks/useLocalization";
import { usePermissions } from "../../hooks/usePermissions";
import { formatApiErrorDetail } from "../../lib/api";
import { formatDate } from "../../lib/date";
import { EMPLOYMENT_PERIOD_TYPES, WAGE_TYPES } from "../../lib/constants";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { AccessDenied } from "../../components/AccessDenied";
import { NotFoundState } from "../../components/NotFoundState";

const FIELDS = [
  { key: "employment_period_type", labelKey: "employmentPeriodType", type: "select", options: EMPLOYMENT_PERIOD_TYPES },
  { key: "employment_period_start", labelKey: "periodStart", type: "date" },
  { key: "employment_period_end", labelKey: "periodEnd", type: "date" },
  { key: "renewal_clause", labelKey: "renewalClause" },
  { key: "workplace", labelKey: "workplace" },
  { key: "job_description", labelKey: "jobDescription" },
  { key: "shift_start", labelKey: "shiftStart" },
  { key: "shift_end", labelKey: "shiftEnd" },
  { key: "break_minutes", labelKey: "breakMinutes", type: "number" },
  { key: "holidays_policy", labelKey: "holidaysPolicy" },
  { key: "overtime_provision", labelKey: "overtimeProvision" },
  { key: "wage_type", labelKey: "wageType", type: "select", options: WAGE_TYPES },
  { key: "basic_wage", labelKey: "basicWage", type: "number" },
  { key: "allowances", labelKey: "allowances" },
  { key: "payment_method", labelKey: "paymentMethod" },
  { key: "payment_date", labelKey: "paymentDate" },
  { key: "deductions", labelKey: "deductions" },
  { key: "bonus", labelKey: "bonus" },
  { key: "retirement_allowance", labelKey: "retirementAllowance" },
  { key: "dismissal_grounds", labelKey: "dismissalGrounds" },
  { key: "consultation_contact", labelKey: "consultationContact" },
  { key: "social_insurance", labelKey: "socialInsurance" },
];

export default function NoticeDetailPage() {
  const { noticeId } = useParams();
  const navigate = useNavigate();
  const { t } = useLocalization();
  const { canManageNotices } = usePermissions();
  const [notice, setNotice] = useState(null);
  const [form, setForm] = useState({});
  const [accessDenied, setAccessDenied] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    noticeApi.get(noticeId)
      .then((r) => { setNotice(r.data); setForm(r.data); setNotFound(false); })
      .catch((err) => { if (err.response?.status === 403) setAccessDenied(true); else if (err.response?.status === 404) setNotFound(true); });
  }, [noticeId]);
  useEffect(() => { load(); }, [load]);

  if (accessDenied) return <AccessDenied resourceLabel={t("notice.title")} />;
  if (notFound) return <NotFoundState testId="notice-not-found" />;
  if (!notice) return <p className="text-sm text-gray-500">{t("common.loading")}</p>;

  const isDraft = notice.status === "DRAFT";

  const handleSave = async () => {
    try {
      const { data } = await noticeApi.updateDraft(noticeId, form);
      setNotice(data);
      toast.success(t("common.save"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleApprove = async () => {
    try {
      const { data } = await noticeApi.approve(noticeId);
      setNotice(data);
      toast.success(t("notice.approveSuccess"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleIssue = async () => {
    try {
      const { data } = await noticeApi.issue(noticeId);
      setNotice(data);
      toast.success(t("notice.issueSuccess"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleUploadScan = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const { data } = await noticeApi.uploadSignedScan(noticeId, file);
      setNotice(data);
      toast.success(t("notice.uploadSuccess"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleRenew = async () => {
    try {
      const { data } = await noticeApi.generate({ employee_id: notice.employee_id, ...notice });
      toast.success(t("notice.generateSuccess"));
      navigate(`/management/notices/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="max-w-3xl space-y-5" data-testid="notice-detail-page">
      <PageHelpBanner pageKey="notice-detail" title="How to use this page">
        <p>Fill in every field while DRAFT, then Save &amp; Approve &rarr; Issue to the employee &rarr; upload the signed scan once they sign it. Print uses the plain printable layout (no nav/sidebar) for a clean paper copy.</p>
      </PageHelpBanner>
      <div className="flex items-center justify-between no-print">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-gray-900">{t("notice.title")}</h2>
          <p className="text-xs text-gray-500">{t("notice.version")} {notice.version}</p>
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge status={notice.status} />
          <button data-testid="notice-print-button" onClick={() => window.print()} className="flex items-center gap-1.5 px-3 py-2 rounded-full border border-gray-300 text-xs font-semibold text-gray-600 hover:bg-gray-50">
            <Printer size={14} /> {t("notice.print")}
          </button>
        </div>
      </div>

      <div className="printable-notice bg-white rounded-xl border border-gray-200 p-6 space-y-3" data-testid="notice-form-panel">
        {FIELDS.map((f) => (
          <div key={f.key} className="grid grid-cols-1 sm:grid-cols-3 gap-1.5 sm:gap-3 sm:items-center">
            <label className="text-xs font-semibold text-gray-500 sm:col-span-1">{t(`notice.${f.labelKey}`)}</label>
            {isDraft && canManageNotices ? (
              f.type === "select" ? (
                <select data-testid={`notice-field-${f.key}`} value={form[f.key] || ""} onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))} className="sm:col-span-2 px-3 py-2 rounded-lg border border-gray-300 text-sm">
                  <option value="">-</option>
                  {f.options.map((opt) => <option key={opt} value={opt}>{t(`notice.${opt}`) === `notice.${opt}` ? opt : t(`notice.${opt}`)}</option>)}
                </select>
              ) : (
                <input data-testid={`notice-field-${f.key}`} type={f.type || "text"} value={form[f.key] ?? ""} onChange={(e) => setForm((s) => ({ ...s, [f.key]: f.type === "number" ? Number(e.target.value) : e.target.value }))} className="sm:col-span-2 px-3 py-2 rounded-lg border border-gray-300 text-sm" />
              )
            ) : (
              <p className="sm:col-span-2 text-sm text-gray-800" data-testid={`notice-value-${f.key}`}>
                {f.type === "date" ? formatDate(notice[f.key]) : (notice[f.key] ?? "-") || "-"}
              </p>
            )}
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 no-print">
        {isDraft && canManageNotices && (
          <>
            <button data-testid="notice-save-button" onClick={handleSave} className="px-5 py-2 rounded-full bg-gray-900 text-white text-sm font-semibold hover:bg-gray-800">{t("common.save")}</button>
            <button data-testid="notice-approve-button" onClick={handleApprove} className="px-5 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700">{t("notice.approve")}</button>
          </>
        )}
        {notice.status === "APPROVED" && canManageNotices && (
          <button data-testid="notice-issue-button" onClick={handleIssue} className="px-5 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700">{t("notice.issue")}</button>
        )}
        {(notice.status === "ISSUED" || notice.status === "ACKNOWLEDGED") && canManageNotices && (
          <>
            <input ref={fileRef} type="file" data-testid="notice-signed-scan-input" className="hidden" onChange={handleUploadScan} />
            <button data-testid="notice-upload-scan-button" onClick={() => fileRef.current?.click()} className="flex items-center gap-1.5 px-5 py-2 rounded-full border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50">
              <Upload size={14} /> {t("notice.uploadSignedScan")}
            </button>
          </>
        )}
        {notice.status === "ACKNOWLEDGED" && canManageNotices && (
          <button data-testid="notice-renew-button" onClick={handleRenew} className="px-5 py-2 rounded-full border border-blue-300 text-blue-700 text-sm font-semibold hover:bg-blue-50">{t("notice.renewOrCorrect")}</button>
        )}
      </div>

      {notice.employee_signature && (
        <p className="text-xs text-gray-500" data-testid="notice-signature-info">{t("notice.signedBy")}: {notice.employee_signature.typed_name} · {notice.employee_signature.signed_at}</p>
      )}
      {notice.signed_scan_path && (
        <p className="text-xs text-gray-500" data-testid="notice-scan-info">{t("notice.signedScanOnFile")}: {notice.signed_scan_filename}</p>
      )}
    </div>
  );
}
